import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function Clients() {
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedClient, setSelectedClient] = useState(null)
  const [appointments, setAppointments] = useState([])
  const [editingNotes, setEditingNotes] = useState(false)
  const [notes, setNotes] = useState('')
  const [nickname, setNickname] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { fetchClients() }, [])

  async function fetchClients() {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'customer')
      .order('full_name')
    if (data) setClients(data)
    setLoading(false)
  }

  async function selectClient(client) {
    setSelectedClient(client)
    setNotes(client.client_notes || '')
    setNickname(client.nickname || '')

    const { data } = await supabase
      .from('appointments')
      .select(`*, services (name, price)`)
      .eq('customer_id', client.id)
      .order('appointment_date', { ascending: false })
    if (data) setAppointments(data)
  }

  async function saveClientInfo() {
    setSaving(true)
    await supabase
      .from('profiles')
      .update({ client_notes: notes, nickname })
      .eq('id', selectedClient.id)
    setSelectedClient({ ...selectedClient, client_notes: notes, nickname })
    setClients(prev => prev.map(c =>
      c.id === selectedClient.id ? { ...c, client_notes: notes, nickname } : c
    ))
    setSaving(false)
    setEditingNotes(false)
  }

  function getStatusColor(status) {
    switch (status) {
      case 'confirmed': return '#D4AF77'
      case 'completed': return '#4A754A'
      case 'cancelled': return '#ef4444'
      default: return '#888'
    }
  }

  const filtered = clients.filter(c =>
    c.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search) ||
    c.nickname?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Clients</h1>
        <span style={styles.count}>{clients.length} clients</span>
      </div>

      <div style={styles.layout}>
        {/* Client list */}
        <div style={styles.listPanel}>
          <input
            style={styles.search}
            placeholder="Search name, email, phone, nickname..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={styles.clientList}>
            {loading ? (
              <div style={styles.empty}>Loading...</div>
            ) : filtered.map(client => (
              <button
                key={client.id}
                style={{
                  ...styles.clientRow,
                  ...(selectedClient?.id === client.id ? styles.clientRowActive : {}),
                }}
                onClick={() => selectClient(client)}>
                <div style={styles.clientAvatar}>
                  {client.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div style={styles.clientInfo}>
                  <div style={styles.clientName}>{client.full_name}</div>
                  {client.nickname && (
                    <div style={styles.clientNickname}>"{client.nickname}"</div>
                  )}
                  <div style={styles.clientContact}>{client.email || client.phone}</div>
                </div>
                {client.has_app && <span style={styles.appDot}>📱</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Client detail */}
        {selectedClient ? (
          <div style={styles.detailPanel}>
            <div style={styles.detailHeader}>
              <div style={styles.detailAvatar}>
                {selectedClient.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={styles.detailName}>{selectedClient.full_name}</div>
                {selectedClient.email && <div style={styles.detailEmail}>{selectedClient.email}</div>}
                {selectedClient.phone && <div style={styles.detailPhone}>{selectedClient.phone}</div>}
                {selectedClient.has_app && <div style={styles.hasApp}>📱 Has Reflections App</div>}
              </div>
            </div>

            {/* Nickname */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionHeader}>
                <div style={styles.infoSectionTitle}>NICKNAME</div>
                <button style={styles.editLink} onClick={() => setEditingNotes(!editingNotes)}>
                  {editingNotes ? 'Cancel' : 'Edit'}
                </button>
              </div>
              {editingNotes ? (
                <input
                  style={styles.input}
                  value={nickname}
                  onChange={e => setNickname(e.target.value)}
                  placeholder="e.g. Kim Facebook"
                />
              ) : (
                <div style={styles.infoValue}>{selectedClient.nickname || '—'}</div>
              )}
            </div>

            {/* Notes */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionHeader}>
                <div style={styles.infoSectionTitle}>PROVIDER NOTES</div>
              </div>
              {editingNotes ? (
                <>
                  <textarea
                    style={styles.textarea}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="e.g. Allergic to bleach, prefers no heat..."
                    rows={3}
                  />
                  <button style={styles.saveBtn} onClick={saveClientInfo} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <div style={styles.infoValue}>{selectedClient.client_notes || '—'}</div>
              )}
            </div>

            {/* Appointment history */}
            <div style={styles.infoSection}>
              <div style={styles.infoSectionTitle}>APPOINTMENT HISTORY</div>
              {appointments.length === 0 ? (
                <div style={styles.empty}>No appointments yet</div>
              ) : appointments.map(apt => (
                <div key={apt.id} style={styles.aptRow}>
                  <div>
                    <div style={styles.aptService}>{apt.services?.name}</div>
                    <div style={styles.aptDate}>{apt.appointment_date}</div>
                  </div>
                  <div style={styles.aptRight}>
                    <div style={styles.aptPrice}>${apt.services?.price}</div>
                    <div style={{
                      ...styles.aptStatus,
                      color: getStatusColor(apt.status),
                      backgroundColor: getStatusColor(apt.status) + '20',
                    }}>
                      {apt.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={styles.detailPanel}>
            <div style={styles.empty}>Select a client to view details</div>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#D4AF77',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    fontFamily: 'Cormorant Garamond, serif',
  },
  count: { fontSize: '12px', color: '#555', letterSpacing: '1px' },
  layout: {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: '24px',
    alignItems: 'start',
  },
  listPanel: {
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  search: {
    width: '100%',
    padding: '14px 16px',
    backgroundColor: '#0a0a0a',
    border: 'none',
    borderBottom: '1px solid #1a1a1a',
    color: '#F5F0E8',
    fontSize: '13px',
    fontFamily: 'Montserrat, sans-serif',
    outline: 'none',
    boxSizing: 'border-box',
  },
  clientList: {
    maxHeight: 'calc(100vh - 280px)',
    overflowY: 'auto',
  },
  clientRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: 'none',
    border: 'none',
    borderBottom: '1px solid #1a1a1a',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.1s',
  },
  clientRowActive: {
    backgroundColor: '#1a1a1a',
    borderLeft: '2px solid #D4AF77',
  },
  clientAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#D4AF77',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '700',
    color: '#000',
    flexShrink: 0,
  },
  clientInfo: { flex: 1, overflow: 'hidden' },
  clientName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#F5F0E8',
    marginBottom: '1px',
  },
  clientNickname: {
    fontSize: '11px',
    color: '#D4AF77',
    fontStyle: 'italic',
    marginBottom: '1px',
  },
  clientContact: {
    fontSize: '11px',
    color: '#555',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  appDot: { fontSize: '14px' },
  detailPanel: {
    backgroundColor: '#111',
    border: '1px solid #1a1a1a',
    borderRadius: '2px',
    padding: '24px',
    minHeight: '400px',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #1a1a1a',
  },
  detailAvatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#D4AF77',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: '700',
    color: '#000',
    flexShrink: 0,
  },
  detailName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#F5F0E8',
    marginBottom: '3px',
  },
  detailEmail: { fontSize: '13px', color: '#555', marginBottom: '2px' },
  detailPhone: { fontSize: '13px', color: '#555' },
  hasApp: { fontSize: '12px', color: '#4A754A', marginTop: '4px' },
  infoSection: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #1a1a1a',
  },
  infoSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  infoSectionTitle: {
    fontSize: '10px',
    fontWeight: '600',
    color: '#555',
    letterSpacing: '2px',
  },
  editLink: {
    background: 'none',
    border: 'none',
    color: '#D4AF77',
    fontSize: '12px',
    cursor: 'pointer',
    letterSpacing: '0.5px',
    fontFamily: 'Montserrat, sans-serif',
  },
  infoValue: { fontSize: '14px', color: '#888' },
  input: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: '2px',
    color: '#F5F0E8',
    fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif',
    outline: 'none',
    marginBottom: '8px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#0a0a0a',
    border: '1px solid #222',
    borderRadius: '2px',
    color: '#F5F0E8',
    fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif',
    outline: 'none',
    resize: 'vertical',
    marginBottom: '8px',
    boxSizing: 'border-box',
  },
  saveBtn: {
    padding: '8px 20px',
    backgroundColor: '#D4AF77',
    color: '#000',
    border: 'none',
    borderRadius: '2px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1.5px',
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
  },
  aptRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #1a1a1a',
  },
  aptService: { fontSize: '14px', color: '#F5F0E8', marginBottom: '2px' },
  aptDate: { fontSize: '12px', color: '#555' },
  aptRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' },
  aptPrice: { fontSize: '14px', fontWeight: '600', color: '#D4AF77' },
  aptStatus: {
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '2px 8px',
    borderRadius: '2px',
  },
  empty: { color: '#444', fontSize: '14px', padding: '24px', textAlign: 'center' },
}